<template>
	<Modal @close="requestClose()" :modalWidth="96">
		<template slot="title">TOUCH Order Calc</template>
		<template slot="body">
			<div class="touch_order_calc">
				<div class="toc_toolbar">
					<span class="toc_views">
						<a v-for="option in views" :key="option.key" href="javascript:;" :class="view === option.key ? 'search_bt' : 'gray_bt'" @click="view = option.key">{{ option.label }}</a>
					</span>
					<span class="toc_buttons">
						<a href="javascript:;" class="search_bt" :class="{ toc_disabled: !safety_edit_count || saving }" @click="saveSafety()">Save Safety Stock ({{ safety_edit_count }})</a>
						<a href="javascript:;" class="gray_bt" @click="resetFinal()">Reset Final</a>
						<a href="javascript:;" class="search_bt" @click="requestDownload()">Download Order Sheet</a>
						<a href="javascript:;" class="gray_bt" @click="requestClose()">Close</a>
					</span>
				</div>

				<div class="toc_summary">
					<span>
						Total Final Order: <b class="toc_total_quantity">{{ formatQuantity(order_matrix.total_quantity) }}</b>
					</span>
					<template v-if="include_value">
						<span>
							FOB Amount: <b class="toc_total_value">{{ formatMoney(order_matrix.total_value) }}</b>
						</span>
						<span> Budget: <input type="text" v-model="budget_text" class="tbl_int toc_budget" placeholder="0.00" /> </span>
						<span v-if="budget_difference !== null" :class="budget_over ? 'toc_over' : 'toc_remaining'">
							{{ budget_over ? 'Over' : 'Remaining' }}: <b>{{ formatMoney(Math.abs(budget_difference)) }}</b>
						</span>
					</template>
					<span v-for="block in order_matrix.blocks" :key="'sum_' + block.key" class="toc_block_total">{{ block.key }}: {{ formatQuantity(block.quantity) }}</span>
				</div>

				<div v-if="pending_action" class="toc_confirm">
					<span>{{ safety_edit_count }} safety stock change(s) are not saved.</span>
					<a href="javascript:;" class="search_bt" @click="confirmPending()">{{ pending_action === 'close' ? 'Discard and Close' : 'Download without Saving' }}</a>
					<a href="javascript:;" class="gray_bt" @click="pending_action = null">Cancel</a>
				</div>
				<div v-if="unmapped_message" class="toc_warning">{{ unmapped_message }}</div>
				<div v-if="view === 'safety'" class="toc_hint">Type in a cell, or in the box next to a length to fill every color of that row. Leave empty to clear.</div>
				<div v-if="view === 'final'" class="toc_hint">Final Order starts from Suggested. Edited cells are highlighted and kept until Reset Final.</div>

				<div v-if="loading" class="toc_loading">Loading...</div>
				<div v-else class="toc_matrix">
					<table v-for="block in matrix.blocks" :key="block.key" class="list_tbl toc_block">
						<thead>
							<tr>
								<th class="toc_row_label">{{ block.key }}</th>
								<th v-for="color in matrix.colors" :key="block.key + color.code">{{ color.label }}</th>
								<th>Total</th>
							</tr>
						</thead>
						<tbody>
							<template v-for="group in block.groups">
								<tr v-for="row in group.rows" :key="row.key" :class="'toc_group_' + group.key">
									<th class="toc_row_label">
										{{ row.label }}
										<input v-if="view === 'safety'" type="text" class="toc_row_fill" placeholder="all" @change="fillRow(row, $event)" />
									</th>
									<td v-for="cell in row.cells" :key="cell.key" :class="cellClass(cell)" :title="cellTitle(cell)">
										<input v-if="isInputCell(cell)" type="text" class="toc_cell_input" :value="inputValue(cell)" @change="onCellChange(cell, $event)" @focus="$event.target.select()" />
										<span v-else>{{ displayValue(cell) }}</span>
									</td>
									<td class="toc_row_total">{{ formatQuantity(rowTotal(row)) }}</td>
								</tr>
							</template>
							<tr class="toc_subtotal">
								<th class="toc_row_label">STTL</th>
								<td v-for="(color, color_index) in matrix.colors" :key="'sttl' + block.key + color.code">{{ formatQuantity(columnTotal(block, color_index)) }}</td>
								<td class="toc_row_total">{{ formatQuantity(blockTotal(block)) }}</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="toc_legend">
					<p>Suggested = Safety Stock − (Available + In Transit + Pending PO), never below 0. Empty Safety Stock means no suggestion.</p>
					<p>Safety Stock is the item's Reorder Level in Item Master. Available = On Hand − quantity held by open sales orders.</p>
					<p>
						In Transit = shipped but not yet received on open (not Closed) shipments. Pending PO = ordered but not yet shipped on open POs. Register each China order as a PO when you place it, and Close POs or shipments whose remaining quantity will
						not arrive.
					</p>
					<p>Final Order is not saved; it goes into the downloaded order sheet. Grey cells have no item (or more than one item) and cannot be edited.</p>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@/components/common/Modal';
import { apiTouchOrderCalc, apiSaveTouchSafetyStock } from '@/api/item/master.js';
import { excelComplicated } from '@/api/util';
import XLSX from 'xlsx-js-style';
import { buildTouchStockSheetCells } from '@/utils/touchStockSheet';
import {
	TOUCH_ORDER_DETAIL_SHEET_NAME,
	TOUCH_ORDER_SHEET_NAME,
	TOUCH_ORDER_SHEET_TITLE,
	TOUCH_ORDER_VIEWS,
	applyRowSafety,
	budgetDifference,
	buildOrderSheetMatrix,
	buildTouchOrderDetailRows,
	buildTouchOrderMatrix,
	cellFinal,
	cellViewValue,
	isEditableCell,
	isFinalOverridden,
	parseFinalInput,
	parseSafetyInput,
	pruneFinalOverrides,
	safetyEditCount,
	safetyEditPayload,
	setFinalOverride,
	setSafetyEdit,
	touchOrderFileName,
	touchOrderNoteText,
	touchOrderUnmappedMessage,
	touchOrderZeroFobMessage,
} from '@/utils/touchOrderSheet';

/**
 * #3368 arkin TOUCH 중국 발주 수량 계산 모달.
 * Safety Stock 은 저장(reorder_level)하고, Final Order 는 저장하지 않고 주문서 다운로드에만 쓴다.
 * 저장하지 않은 Safety 변경이 있으면 닫기·다운로드 전에 모달 안에서 확인한다(브라우저 confirm 을 쓰지 않는다).
 */
export default {
	components: {
		Modal,
	},
	props: {
		include_value: { type: Boolean, default: false },
	},
	data: function() {
		return {
			views: TOUCH_ORDER_VIEWS,
			view: 'final',
			loading: true,
			saving: false,
			matrix: buildTouchOrderMatrix([], { include_value: this.include_value }),
			safety_edits: {},
			final_overrides: {},
			budget_text: '',
			pending_action: null,
		};
	},
	computed: {
		safety_edit_count() {
			return safetyEditCount(this.safety_edits);
		},
		order_matrix() {
			return buildOrderSheetMatrix(this.matrix, this.safety_edits, this.final_overrides);
		},
		budget_difference() {
			return this.include_value ? budgetDifference(this.budget_text, this.order_matrix.total_value) : null;
		},
		budget_over() {
			return this.budget_difference !== null && this.budget_difference < 0;
		},
		unmapped_message() {
			return touchOrderUnmappedMessage(this.matrix);
		},
	},
	created() {
		this.load();
	},
	methods: {
		async load() {
			this.loading = true;
			try {
				const { data } = await apiTouchOrderCalc();
				if (!Array.isArray(data)) {
					this.$toast.error('TOUCH Order Calc: unexpected response.');
					return;
				}
				this.matrix = buildTouchOrderMatrix(data, { include_value: this.include_value });
				// 사용자가 고친 Final 은 같은 item_id 로 다시 연결한다. 사라진 품목만 버린다.
				this.final_overrides = pruneFinalOverrides(this.matrix, this.final_overrides);
			} finally {
				this.loading = false;
			}
		},
		isInputCell(cell) {
			return (this.view === 'safety' || this.view === 'final') && isEditableCell(cell);
		},
		displayValue(cell) {
			const value = cellViewValue(cell, this.view, this.safety_edits, this.final_overrides);
			if (value === null || value === undefined) return '';
			if (cell.status === 'empty') return '';
			return this.formatQuantity(value);
		},
		inputValue(cell) {
			const value = cellViewValue(cell, this.view, this.safety_edits, this.final_overrides);
			return value === null || value === undefined ? '' : String(value);
		},
		cellClass(cell) {
			const classes = ['toc_cell'];
			if (cell.status !== 'single') classes.push('toc_cell_disabled');
			if (this.view === 'safety' && isEditableCell(cell) && Object.prototype.hasOwnProperty.call(this.safety_edits, cell.item_id)) classes.push('toc_cell_dirty');
			if (this.view === 'final' && isFinalOverridden(cell, this.final_overrides)) classes.push('toc_cell_overridden');
			if (this.view === 'final' && cellFinal(cell, this.safety_edits, this.final_overrides) > 0) classes.push('toc_cell_order');
			return classes;
		},
		cellTitle(cell) {
			if (cell.status === 'empty') return 'No item for this length and color.';
			if (cell.status === 'multiple') return `More than one item (${cell.item_code}). Values are totals and cannot be edited here.`;
			return cell.item_code;
		},
		onCellChange(cell, event) {
			if (this.view === 'safety') {
				const parsed = parseSafetyInput(event.target.value);
				if (!parsed.ok) {
					this.$toast.error('Safety Stock must be a whole number of 0 or more (empty to clear).');
					event.target.value = this.inputValue(cell);
					return;
				}
				this.safety_edits = setSafetyEdit(this.safety_edits, cell, parsed.value);
			} else if (this.view === 'final') {
				const parsed = parseFinalInput(event.target.value);
				if (!parsed.ok) {
					this.$toast.error('Final Order must be a whole number of 0 or more.');
					event.target.value = this.inputValue(cell);
					return;
				}
				this.final_overrides = setFinalOverride(this.final_overrides, cell, parsed.value);
			}
		},
		fillRow(row, event) {
			const parsed = parseSafetyInput(event.target.value);
			if (!parsed.ok) {
				this.$toast.error('Safety Stock must be a whole number of 0 or more (empty to clear).');
				return;
			}
			this.safety_edits = applyRowSafety(this.safety_edits, row, parsed.value);
			event.target.value = '';
		},
		rowTotal(row) {
			return row.cells.reduce((sum, cell) => sum + (cellViewValue(cell, this.view, this.safety_edits, this.final_overrides) || 0), 0);
		},
		columnTotal(block, color_index) {
			return block.groups.reduce((sum, group) => sum + group.rows.reduce((row_sum, row) => row_sum + (cellViewValue(row.cells[color_index], this.view, this.safety_edits, this.final_overrides) || 0), 0), 0);
		},
		blockTotal(block) {
			return block.groups.reduce((sum, group) => sum + group.rows.reduce((row_sum, row) => row_sum + this.rowTotal(row), 0), 0);
		},
		resetFinal() {
			this.final_overrides = {};
		},
		async saveSafety() {
			if (!this.safety_edit_count || this.saving) return;
			this.saving = true;
			try {
				const { data } = await apiSaveTouchSafetyStock(safetyEditPayload(this.safety_edits));
				if (data && data.result) {
					this.$toast.success(`Safety Stock saved (${data.count} item(s)).`);
					this.safety_edits = {};
					await this.load();
				} else {
					this.$toast.error((data && data.msg) || 'Safety Stock was not saved.');
				}
			} finally {
				this.saving = false;
			}
		},
		requestClose() {
			if (this.safety_edit_count) {
				this.pending_action = 'close';
				return;
			}
			this.$emit('close');
		},
		requestDownload() {
			if (this.safety_edit_count) {
				this.pending_action = 'download';
				return;
			}
			this.download();
		},
		confirmPending() {
			const action = this.pending_action;
			this.pending_action = null;
			if (action === 'close') this.$emit('close');
			else if (action === 'download') this.download();
		},
		download() {
			const report_date = new Date();
			const order_matrix = this.order_matrix;
			const cells = buildTouchStockSheetCells(order_matrix, { report_date, title: TOUCH_ORDER_SHEET_TITLE, note_text: touchOrderNoteText(this.matrix, order_matrix) });
			const detail_rows = buildTouchOrderDetailRows(this.matrix, this.safety_edits, this.final_overrides);

			XLSX.writeFile(
				{
					SheetNames: [TOUCH_ORDER_SHEET_NAME, TOUCH_ORDER_DETAIL_SHEET_NAME],
					Sheets: {
						[TOUCH_ORDER_SHEET_NAME]: excelComplicated(cells),
						[TOUCH_ORDER_DETAIL_SHEET_NAME]: XLSX.utils.aoa_to_sheet(detail_rows),
					},
				},
				touchOrderFileName(report_date),
			);

			// 주문서에서 조용히 빠지거나(미매핑) 금액이 조용히 줄어드는(FOB 0) 수량은 반드시 알린다.
			[touchOrderUnmappedMessage(this.matrix), touchOrderZeroFobMessage(order_matrix)].filter(Boolean).forEach(message => this.$toast.warning(message));
		},
		formatQuantity(value) {
			return Number(value || 0).toLocaleString('en-US');
		},
		formatMoney(value) {
			return '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		},
	},
};
</script>

<style lang="scss" scoped>
.touch_order_calc {
	min-width: 900px;
}
.toc_toolbar {
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	margin: 6px 0;
	a {
		margin-right: 4px;
	}
}
.toc_disabled {
	opacity: 0.5;
	pointer-events: none;
}
.toc_summary {
	margin: 6px 0;
	span {
		margin-right: 18px;
	}
	.toc_budget {
		width: 110px;
		text-align: right;
	}
	.toc_over {
		color: #c00;
	}
	.toc_remaining {
		color: #070;
	}
}
.toc_confirm {
	background: #fff4d6;
	border: 1px solid #e0b84c;
	padding: 6px 10px;
	margin: 6px 0;
	span {
		margin-right: 12px;
	}
	a {
		margin-right: 6px;
	}
}
.toc_warning {
	background: #fde8e8;
	border: 1px solid #e3a0a0;
	padding: 6px 10px;
	margin: 6px 0;
}
.toc_hint {
	color: #555;
	margin: 4px 0;
}
.toc_loading {
	padding: 30px;
	text-align: center;
}
.toc_matrix {
	max-height: 62vh;
	overflow: auto;
}
.toc_block {
	margin-bottom: 12px;
	th,
	td {
		padding: 2px 4px;
		white-space: nowrap;
		text-align: center;
	}
	.toc_row_label {
		text-align: left;
		min-width: 90px;
	}
	.toc_row_total {
		font-weight: bold;
	}
	.toc_subtotal td,
	.toc_subtotal th {
		font-weight: bold;
		border-top: 2px solid #666;
	}
}
.toc_group_4X4 td {
	background: #fdeada;
}
.toc_group_13X4 td {
	background: #fcd5b5;
}
.toc_block td.toc_cell_disabled {
	background: #e6e6e6;
	color: #888;
}
.toc_block td.toc_cell_dirty {
	background: #fff3a8;
}
.toc_block td.toc_cell_overridden {
	background: #cfe8ff;
}
.toc_cell_order {
	font-weight: bold;
}
.toc_cell_input {
	width: 48px;
	text-align: right;
}
.toc_row_fill {
	width: 40px;
	margin-left: 4px;
}
.toc_legend {
	margin: 8px 0;
	color: #444;
	font-size: 12px;
	p {
		margin: 2px 0;
	}
}
</style>
